import { Assets, Container, Graphics, Sprite } from 'pixi.js';
import type { Texture } from 'pixi.js';
import type { Difficulty, LobbyPlayer, ServerMessage } from '@nanocars/shared';
import { LOGICAL_WIDTH, type Scene, type SceneManager } from './SceneManager';
import { UI_PATHS } from '../assets/manifest';
import { COLORS, PLAYER_COLORS, colourForSlot, lighten } from '../ui/theme';
import { makeImageButton, makeLabel } from '../ui/widgets';
import { FocusRing, type FocusItem } from '../ui/focus';
import { session } from '../state/Session';
import { Audio, createMuteButton, MUSIC_LOBBY_GAIN } from '../audio/Audio';
import { TitleScene } from './TitleScene';
import { RaceScene } from './RaceScene';

const SEAT_COUNT = 4;
const MUTE_BUTTON_MARGIN = 16;
const CARD_WIDTH = 272;
const CARD_HEIGHT = Math.round((CARD_WIDTH * 768) / 596);
const CARD_GAP = 24;
const CARD_MARGIN = (LOGICAL_WIDTH - (SEAT_COUNT * CARD_WIDTH + (SEAT_COUNT - 1) * CARD_GAP)) / 2;
const SEAT_Y = 136;
const CONTROLS_Y = SEAT_Y + CARD_HEIGHT + 14;
const COPIED_DURATION_S = 1.5;
const AI_MIN = 0;
const AI_MAX = 5;
const DIFFICULTIES: readonly Difficulty[] = ['easy', 'normal', 'hard'];

function seatX(index: number): number {
  return CARD_MARGIN + index * (CARD_WIDTH + CARD_GAP);
}

// Lobby: room code (click to copy), 4 seat cards, host-only AI count /
// difficulty controls, Start (host) and Leave. Re-renders in full on every
// `lobby` message from the server rather than trying to patch a diff -
// simplest correct thing given the message already carries full state.
export class LobbyScene implements Scene {
  readonly name = 'lobby';
  readonly container = new Container();
  private readonly content = new Container();

  private cardTexture: Texture | null = null;
  private buttonTexture: Texture | null = null;
  private focusRing: FocusRing | null = null;
  private unsubscribers: Array<() => void> = [];
  private copiedTimer = 0;
  private copiedLabel: ReturnType<typeof makeLabel> | null = null;

  constructor(private readonly scenes: SceneManager) {}

  async enter(): Promise<void> {
    Audio.init();
    // music.play() no-ops until music_loop is decoded, so this await matters
    // (not just fire-and-forget like RaceScene's re-entrant loadAll call) -
    // this is the one scene that actually starts the loop.
    await Audio.loadAll();
    Audio.music.play();
    Audio.music.setGain(MUSIC_LOBBY_GAIN);

    this.container.addChild(this.content);
    this.cardTexture = await Assets.load(UI_PATHS.card);
    this.buttonTexture = await Assets.load(UI_PATHS.button);
    this.focusRing = new FocusRing(this.container, session.input);

    this.unsubscribers.push(
      session.net.on('lobby', (msg) => {
        session.lobby = msg.lobby;
        this.render();
      }),
      session.net.on('raceStart', (msg) => {
        session.lastRaceStart = msg;
        void this.scenes.go(new RaceScene(this.scenes));
      }),
    );

    this.render();
  }

  exit(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers = [];
    this.focusRing?.dispose();
    this.content.removeChildren();
  }

  update(dt: number): void {
    if (this.copiedTimer > 0) {
      this.copiedTimer -= dt;
      if (this.copiedLabel) this.copiedLabel.visible = this.copiedTimer > 0;
    }
  }

  resize(_w: number, _h: number): void {
    // Fixed 1280x720 logical layout.
  }

  private render(): void {
    this.content.removeChildren();

    const muteButton = createMuteButton();
    muteButton.x = LOGICAL_WIDTH - muteButton.width - MUTE_BUTTON_MARGIN;
    muteButton.y = MUTE_BUTTON_MARGIN;
    this.content.addChild(muteButton);

    const lobby = session.lobby;
    if (!lobby || !this.cardTexture || !this.buttonTexture) {
      const loading = makeLabel('Loading lobby...', { fontSize: 22, fill: COLORS.textLight });
      loading.anchor.set(0.5);
      loading.x = LOGICAL_WIDTH / 2;
      loading.y = 300;
      this.content.addChild(loading);
      this.focusRing?.setItems([]);
      return;
    }

    const focusItems: FocusItem[] = [];
    const isHost = lobby.players.some((p) => p.id === session.playerId && p.isHost);

    this.buildHeader(lobby.code);

    for (let i = 0; i < SEAT_COUNT; i++) {
      const player = lobby.players[i] ?? null;
      const { card, items } = this.buildSeatCard(i, player, lobby.players);
      this.content.addChild(card);
      focusItems.push(...items);
    }

    focusItems.push(...this.buildControls(lobby, isHost));

    this.focusRing?.setItems(focusItems);
  }

  private buildHeader(code: string): void {
    const label = makeLabel('ROOM CODE', { fontSize: 16, fill: COLORS.textLight });
    label.anchor.set(0.5, 0);
    label.x = LOGICAL_WIDTH / 2;
    label.y = 12;
    this.content.addChild(label);

    const codeContainer = new Container();
    codeContainer.x = LOGICAL_WIDTH / 2;
    codeContainer.y = 34;
    codeContainer.eventMode = 'static';
    codeContainer.cursor = 'pointer';
    codeContainer.on('pointertap', () => void this.handleCopyCode());

    const codeText = makeLabel(code, {
      fontSize: 56,
      fontWeight: '900',
      fill: COLORS.textLight,
      letterSpacing: 10,
    });
    codeText.anchor.set(0.5, 0);
    codeContainer.addChild(codeText);
    this.content.addChild(codeContainer);

    this.copiedLabel = makeLabel('Copied!', { fontSize: 16, fill: COLORS.accentYellow });
    this.copiedLabel.anchor.set(0, 0.5);
    this.copiedLabel.x = codeContainer.x + codeText.width / 2 + 16;
    this.copiedLabel.y = codeContainer.y + codeText.height / 2;
    this.copiedLabel.visible = this.copiedTimer > 0;
    this.content.addChild(this.copiedLabel);
  }

  private async handleCopyCode(): Promise<void> {
    const code = session.lobby?.code;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      this.copiedTimer = COPIED_DURATION_S;
      if (this.copiedLabel) this.copiedLabel.visible = true;
    } catch {
      // Clipboard access can be denied/unavailable; the code is still
      // visible on screen to copy by hand.
    }
  }

  private buildSeatCard(
    index: number,
    player: LobbyPlayer | null,
    allPlayers: readonly LobbyPlayer[],
  ): { card: Container; items: FocusItem[] } {
    const card = new Container();
    card.x = seatX(index);
    card.y = SEAT_Y;

    const face = new Sprite(this.cardTexture!);
    face.width = CARD_WIDTH;
    face.height = CARD_HEIGHT;
    card.addChild(face);

    if (!player) {
      card.alpha = 0.55;
      const empty = makeLabel('Empty seat', { fontSize: 18, fill: COLORS.outline });
      empty.anchor.set(0.5);
      empty.x = CARD_WIDTH / 2;
      empty.y = CARD_HEIGHT / 2;
      card.addChild(empty);
      return { card, items: [] };
    }

    card.alpha = player.connected ? 1 : 0.55;
    const isOwn = player.id === session.playerId;
    const items: FocusItem[] = [];

    const nameText = makeLabel(player.name, { fontSize: 20, fontWeight: '800', fill: COLORS.textDark });
    nameText.anchor.set(0.5, 0);
    nameText.x = CARD_WIDTH / 2;
    nameText.y = 28;
    card.addChild(nameText);

    if (player.isHost) {
      const crown = makeLabel('\u{1F451}', { fontSize: 26 });
      crown.anchor.set(0.5);
      crown.x = CARD_WIDTH - 36;
      crown.y = 34;
      card.addChild(crown);
    }

    const swatch = new Graphics().circle(CARD_WIDTH / 2, 120, 28).fill(colourForSlot(player.colour));
    swatch.stroke({ width: 3, color: COLORS.outline });
    card.addChild(swatch);
    if (isOwn) {
      swatch.eventMode = 'static';
      swatch.cursor = 'pointer';
      swatch.on('pointertap', () => this.cycleOwnColour(allPlayers));
      items.push({ container: swatch, activate: () => this.cycleOwnColour(allPlayers) });
    }

    const readyChip = makeToggleChip(player.ready ? 'Ready' : 'Not ready', player.ready, isOwn, () =>
      this.toggleOwnReady(),
      140,
    );
    readyChip.x = CARD_WIDTH / 2 - 70;
    readyChip.y = 176;
    card.addChild(readyChip);
    if (isOwn) items.push({ container: readyChip, activate: () => this.toggleOwnReady() });

    return { card, items };
  }

  // `LobbyPlayer.colour` is a slot INDEX (see theme.ts/PLAYER_COLORS and
  // server/room.ts), not a colour value - cycle over indices and send the
  // index back, matching what the server validates/stores.
  private cycleOwnColour(allPlayers: readonly LobbyPlayer[]): void {
    const me = allPlayers.find((p) => p.id === session.playerId);
    if (!me) return;
    const used = new Set(allPlayers.filter((p) => p.id !== me.id).map((p) => p.colour));
    const currentIndex = me.colour;
    for (let step = 1; step <= PLAYER_COLORS.length; step++) {
      const candidate = (currentIndex + step) % PLAYER_COLORS.length;
      if (!used.has(candidate)) {
        session.net.send({ t: 'setColour', colour: candidate });
        return;
      }
    }
  }

  private toggleOwnReady(): void {
    const me = session.lobby?.players.find((p) => p.id === session.playerId);
    if (!me) return;
    session.net.send({ t: 'ready', ready: !me.ready });
  }

  private buildControls(lobby: { aiCount: number; difficulty: Difficulty }, isHost: boolean): FocusItem[] {
    const items: FocusItem[] = [];

    const aiLabel = makeLabel('AI Racers', { fontSize: 16, fill: COLORS.textLight });
    aiLabel.x = CARD_MARGIN;
    aiLabel.y = CONTROLS_Y;
    aiLabel.alpha = isHost ? 1 : 0.6;
    this.content.addChild(aiLabel);

    const minusBtn = makeStepperButton('-', isHost && lobby.aiCount > AI_MIN, () =>
      this.setAi(lobby.aiCount - 1, lobby.difficulty),
    );
    minusBtn.x = CARD_MARGIN;
    minusBtn.y = CONTROLS_Y + 26;
    this.content.addChild(minusBtn);
    if (isHost) items.push({ container: minusBtn, activate: () => this.setAi(lobby.aiCount - 1, lobby.difficulty) });

    const countText = makeLabel(String(lobby.aiCount), { fontSize: 22, fontWeight: '800', fill: COLORS.textLight });
    countText.anchor.set(0.5);
    countText.x = CARD_MARGIN + 70;
    countText.y = CONTROLS_Y + 46;
    this.content.addChild(countText);

    const plusBtn = makeStepperButton('+', isHost && lobby.aiCount < AI_MAX, () =>
      this.setAi(lobby.aiCount + 1, lobby.difficulty),
    );
    plusBtn.x = CARD_MARGIN + 100;
    plusBtn.y = CONTROLS_Y + 26;
    this.content.addChild(plusBtn);
    if (isHost) items.push({ container: plusBtn, activate: () => this.setAi(lobby.aiCount + 1, lobby.difficulty) });

    const difficultyLabel = makeLabel('Difficulty', { fontSize: 16, fill: COLORS.textLight });
    difficultyLabel.x = CARD_MARGIN + 180;
    difficultyLabel.y = CONTROLS_Y;
    difficultyLabel.alpha = isHost ? 1 : 0.6;
    this.content.addChild(difficultyLabel);

    let dx = CARD_MARGIN + 180;
    for (const diff of DIFFICULTIES) {
      const chip = makeToggleChip(capitalize(diff), lobby.difficulty === diff, isHost, () =>
        this.setAi(lobby.aiCount, diff),
        90,
      );
      chip.x = dx;
      chip.y = CONTROLS_Y + 26;
      this.content.addChild(chip);
      if (isHost) items.push({ container: chip, activate: () => this.setAi(lobby.aiCount, diff) });
      dx += 100;
    }

    if (isHost) {
      // Same cream button.png art as Title/Join, with a red label and a
      // slight red tint on the sprite so it still reads as the primary
      // action against the neutral "Leave" button below it.
      const startBtn = makeImageButton(this.buttonTexture!, 'Start Race', () => this.handleStart(), {
        width: 220,
        height: 80,
        textFill: COLORS.accentRed,
        tint: lighten(COLORS.accentRed, 0.5),
      });
      startBtn.x = LOGICAL_WIDTH - CARD_MARGIN - 220;
      startBtn.y = CONTROLS_Y;
      this.content.addChild(startBtn);
      items.push({ container: startBtn, activate: () => this.handleStart() });
    } else {
      const waiting = makeLabel('Waiting for host to start...', { fontSize: 16, fill: COLORS.textLight });
      waiting.x = LOGICAL_WIDTH - CARD_MARGIN - 260;
      waiting.y = CONTROLS_Y + 20;
      this.content.addChild(waiting);
    }

    const leaveBtn = makeImageButton(this.buttonTexture!, 'Leave', () => this.handleLeave(), {
      width: 220,
      height: 80,
    });
    leaveBtn.x = LOGICAL_WIDTH - CARD_MARGIN - 220;
    leaveBtn.y = CONTROLS_Y + 84;
    this.content.addChild(leaveBtn);
    items.push({ container: leaveBtn, activate: () => this.handleLeave() });

    return items;
  }

  private setAi(count: number, difficulty: Difficulty): void {
    const clamped = Math.min(AI_MAX, Math.max(AI_MIN, count));
    session.net.send({ t: 'setAi', count: clamped, difficulty });
  }

  private handleStart(): void {
    session.net.send({ t: 'start' });
  }

  private handleLeave(): void {
    session.net.send({ t: 'leave' });
    session.lobby = null;
    void this.scenes.go(new TitleScene(this.scenes));
  }
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

function makeToggleChip(
  label: string,
  selected: boolean,
  enabled: boolean,
  onClick: () => void,
  width = 90,
): Container {
  const height = 40;
  const container = new Container();
  const bg = new Graphics().roundRect(0, 0, width, height, 10).fill(selected ? COLORS.accentYellow : COLORS.panel);
  bg.stroke({ width: 2, color: COLORS.outline });
  const text = makeLabel(label, { fontSize: 15, fontWeight: '800', fill: COLORS.textDark });
  text.anchor.set(0.5);
  text.x = width / 2;
  text.y = height / 2;
  container.addChild(bg, text);
  container.alpha = enabled ? 1 : 0.5;
  if (enabled) {
    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.on('pointertap', onClick);
  }
  return container;
}

function makeStepperButton(label: string, enabled: boolean, onClick: () => void): Container {
  const size = 40;
  const container = new Container();
  const bg = new Graphics().roundRect(0, 0, size, size, 8).fill(COLORS.panel);
  bg.stroke({ width: 2, color: COLORS.outline });
  const text = makeLabel(label, { fontSize: 22, fontWeight: '900', fill: COLORS.textDark });
  text.anchor.set(0.5);
  text.x = size / 2;
  text.y = size / 2 - 1;
  container.addChild(bg, text);
  container.alpha = enabled ? 1 : 0.5;
  if (enabled) {
    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.on('pointertap', onClick);
  }
  return container;
}
